import { useState, useContext, useEffect, useRef } from 'react';
import { BookingContext } from '../../shared/contexts/Booking.context';
import { LanguageContext } from '../../shared/contexts/Language.context';
import bookingSearchFormRequest from '../../services/bookingSearchForm.service';
import { useRouter } from 'next/router';
import { showError } from '../../shared/utils/utils';
import dayjs from 'dayjs';
import { ThemeContext } from '../../shared/contexts/Theme.context';
import * as GtmUtils from '../../shared/utils/GtmUtils';
import { HB_SESSION, deleteCookie, setCookie } from '../../shared/utils/cookies';
import { CHANNEL_KEY, METRIC_ORIGIN_KEY } from '../../shared/utils/constants';

function useBookingSearchForm() {
  const router = useRouter();
  const { scope, source } = router.query;
  const { state: bookingContextState, actions: bookingContextActions } = useContext(BookingContext);
  const { state: languageContextState } = useContext(LanguageContext);
  const { state: themeState } = useContext(ThemeContext);
  // Helper to build target paths with allowed query params
  function buildRoute(basePath) {
    try {
      const params = new URLSearchParams();
      if (scope === 'parent') params.set('scope', 'parent');
      if (source === 'group-checkin') params.set('source', 'group-checkin');
      const qs = params.toString();
      return qs ? `${basePath}?${qs}` : basePath;
    } catch (e) {
      return basePath;
    }
  }
  const [state, setState] = useState({
    loading: !bookingContextState.bookingSearchForm,
    ready: false,
    formInProgress: false,
    isAutoSearch: true,
  });
  const currentLangRef = useRef();
  const formRef = useRef();

  const actions = {
    setFormInstance: ref => {
      formRef.current = ref;
    },
    getBookingSearchForm: () => {
      if (!router.isReady) return;

      if (
        bookingContextState.hotelId &&
        bookingContextState.hotelId == router.query.hotelId &&
        (!bookingContextState.bookingSearchForm ||
          currentLangRef.current != languageContextState.selectedLanguage)
      ) {
        currentLangRef.current = languageContextState.selectedLanguage;
        updateState({ loading: true });
        bookingSearchFormRequest(bookingContextState.hotelId, currentLangRef.current)
          .then(bookingSearchForm => {
            if (bookingSearchForm.lang === currentLangRef.current) {
              delete bookingSearchForm.lang;
              bookingContextActions.setBookingSearchForm(bookingSearchForm);
              if (bookingSearchForm?.extraFields) {
                updateState({
                  loading: false,
                  extraFields: bookingSearchForm?.extraFields?.fields,
                  chain: bookingSearchForm?.extraFields?.chain,
                });
              } else updateState({ loading: false });

              actions.checkAutoSearchParameters();

              if (themeState.isKiosk) {
                actions.setKioskDefaultValues();
              }
            }
          })
          .catch(error => {
            showError(error);
          });
      }
    },
 
    setBookingSearchFormValues: bookingSearchFormValues => {    
      if (themeState.isKiosk) {
        bookingContextActions.configNewCheckin(
          bookingContextState.hotelId,
          bookingSearchFormValues
        );
      }
      bookingContextActions.setBookingSearchFormValues(bookingSearchFormValues);
      bookingContextActions.setBookingSearchResults(null);
      let processType = localStorage.getItem('process-type');
      switch (processType) {
        case 'checkout':
          router.push(
            '/hotel/[hotelId]/checkout/checkoutBookingSearch',
            `/hotel/${bookingContextState.hotelId}/checkout/checkoutBookingSearch`
          );
          break;
        case 'checkin':
        default:
          if(bookingContextState.bookingSearchForm?.enabledCheckin2fa){
            router.replace(
              '/hotel/[hotelId]/bookingSearch2faForm',
              buildRoute(`/hotel/${bookingContextState.hotelId}/bookingSearch2faForm`)
            );
          } else {
            if (state.isAutoSearch) {
              router.replace(
                '/hotel/[hotelId]/bookingSearch',
                buildRoute(`/hotel/${bookingContextState.hotelId}/bookingSearch`)
              );
            } else {
              router.push(
                '/hotel/[hotelId]/bookingSearch',
                buildRoute(`/hotel/${bookingContextState.hotelId}/bookingSearch`)
              );
            }
          }
      }
    },

    setCurrentFormValues: currentFormValues => {
      const ready = bookingContextState.bookingSearchForm?.fields.every(
        field => Boolean(currentFormValues[field.key]) || !field.required
      );
      updateState({ currentFormValues });
      if (ready != state.ready) {
        updateState({ ready });
      }
      if (!state.formInProgress) updateState({ formInProgress: true });
    },

    setInitialValues: () => {
      const initialValues = themeState.isKiosk
        ? { entranceDate: dayjs() }
        : bookingContextState.bookingSearchFormValues || {};
        
      const fields = bookingContextState.bookingSearchForm?.fields || [];
      const formattedValues = Object.keys(initialValues).reduce((formattedValues, key) => {
        const field = fields.find(field => field.key == key);
        if (field) {
          let value = initialValues[key];
          if (field.type == 'date') {
            value = dayjs(value);
          }
          formattedValues[key] = value;
        }

        return formattedValues;
      }, {});

      formRef.current.setFieldsValue(formattedValues);
      actions.setCurrentFormValues(formattedValues);
    },

    checkAutoSearchParameters: () => {
      let windowLocationSearch;
      try {
        windowLocationSearch = window.location.search.replace('?utm_source', '&utm_source');
      } catch (error) {}
      const searchParams = new URLSearchParams(windowLocationSearch);
      if (Array.from(searchParams).length <= 0) {
        updateState({ isAutoSearch: false });
        return;
      }

      const bookingSearchFormValues = {};
      let autoSearchEnabled = false;
      let hbSession,
        metricOrigin = null;
      searchParams.forEach((value, key) => {
        if (key === 'autoSearch') {
          autoSearchEnabled = true;
          bookingSearchFormValues[key] = true;
        } else if (key === HB_SESSION) {
          hbSession = value;
        } else if (key === METRIC_ORIGIN_KEY) {
          metricOrigin = value;
        } else {
          bookingSearchFormValues[key] = value;
        }
      });

      if (autoSearchEnabled) {
        actions.setBookingSearchFormValues(bookingSearchFormValues);
      } else {
        updateState({ isAutoSearch: false });
        deleteCookie(HB_SESSION);
        localStorage.removeItem(METRIC_ORIGIN_KEY);
      }

      if (hbSession) setCookie(HB_SESSION, hbSession);
      if (metricOrigin) localStorage.setItem(METRIC_ORIGIN_KEY, metricOrigin);
    },

    setKioskDefaultValues: () => {
      actions.setCurrentFormValues({ entranceDate: dayjs() });
    },

    goToBookingSelector: () => {
      let processType = localStorage.getItem('process-type');
      switch (processType) {
        /*case 'checkout':
          router.push(
            '/hotel/[hotelId]/checkout/bookingSelector',
            `/hotel/${bookingContextState.hotelId}/checkout/bookingSelector`
          );
          break;*/
        case 'checkin':
        default:
            router.push(
              '/hotel/[hotelId]/bookingSelector',
              buildRoute(`/hotel/${bookingContextState.hotelId}/bookingSelector`)
            );
      }
    },
  };

  useEffect(() => {
    actions.getBookingSearchForm();
    const { hotelId, ...customParams } = router.query;
    if (customParams && Object.keys(customParams).length) {
      bookingContextActions.setBookingCustomParameters(customParams, 'allGuests');
      const { channel } = customParams;
      if (channel) {
        localStorage.setItem(CHANNEL_KEY, channel);
      } else {
        localStorage.removeItem(CHANNEL_KEY);
      }
    }
  }, [bookingContextState.hotelId, languageContextState.selectedLanguage, router.isReady]);

  useEffect(() => {
    if (!state.formInProgress && bookingContextState.bookingSearchFormValues && formRef?.current) {
      actions.setInitialValues();
    }
  }, [bookingContextState.bookingSearchFormValues, formRef?.current]);

  useEffect(() => {
    updateState({ loading: !bookingContextState.bookingSearchForm });
  }, [bookingContextState.bookingSearchForm]);

  useEffect(() => {
    if (bookingContextState.hotelId) {
      GtmUtils.customPageView({
        hotelName: bookingContextState.hotelId,
        stepName: 'bookingSearchForm',
        chainName: themeState.chainName,
      });
    }
  }, [bookingContextState.hotelId]);

  function updateState(newState) {
    setState(previousState => ({ ...previousState, ...newState }));
  }

  return { state, actions };
}

export default useBookingSearchForm;
